import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Event as RouterEvent, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { User } from '@auth0/auth0-angular';
import {
    BehaviorSubject,
    Subject,
    combineLatest,
    filter,
    takeUntil,
} from 'rxjs';
import {
    GetUserMessageTopics200Response,
    NotificationsService,
} from '@viewbid/ts-angular-client';
import {
    VbSupportHoursRow,
    VbSupportLot,
    VbSupportMessage,
    VbSupportSendError,
    VbSupportSubject,
} from '@viewbid/ui';
import { CrudService } from '@/shared/services/crud.service';
import { AppState } from 'src/app/viewbid/state/app-state';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { SelectedLotState } from 'src/app/viewbid/modules/auctions/state/selected-lot-state/selected-lot.reducer';
import { selectSelectedLotState } from 'src/app/viewbid/modules/auctions/state/selected-lot-state/selected-lot.selectors';
import { ChatBoxService } from './service/chatbox.service';
import {
    FALLBACK_SUPPORT_SUBJECTS,
    mapTopicsToSupportSubjects,
} from '../../utils/support-ui-mapper.util';

const USER_MESSAGES_ENDPOINT: string = '/api/notifications/user-messages';

/** Real support line (the old widget's number, in the panel's display style). */
const SUPPORT_PHONE_DISPLAY: string = '+1 (902) 461-1010';
const SUPPORT_PHONE_HREF: string = 'tel:+19024611010';

/** Error copy by cause — the handoff's table, with the real support line. */
const SEND_ERROR_OFFLINE: string =
    "You're offline right now. Your draft is saved, so send it again once " +
    "you're back on.";
const SEND_ERROR_SERVER: string =
    "We couldn't reach Viewbid. Your draft is saved. Try again in a " +
    `moment, or call ${SUPPORT_PHONE_DISPLAY}.`;
const SEND_ERROR_SESSION: string =
    'Your session expired before this sent. Sign in again and your draft ' +
    'will be waiting.';
const SEND_ERROR_RATE_LIMIT: string =
    "You've sent several messages in a row. Give us a minute to catch up, " +
    'then send this one.';

/**
 * Host for the redesigned support widget (`vb-support-widget` from
 * @viewbid/ui) — replaces the legacy chatbox/messenger pair while keeping
 * the `vb-chatbox` selector and `ChatBoxService.open()` contract, so the
 * header shells and the lot/invoice "message" buttons need no changes.
 *
 * Owns everything the presentational widget doesn't: route context (lot
 * pages bind [lot], which adds a "Question about this lot" row to the
 * subject list; only service-triggered opens — "Message seller" — preset
 * that subject and skip the list, and view-invoice pages likewise preset
 * "Issue with an order" on service-triggered opens), the
 * subject rows from the user-message-topics API, the session user's FROM
 * line, and the send round trip against the user-messages endpoint with
 * the handoff's per-cause error copy. A 401 relabels the primary button to
 * "Sign in and send"; clicking it then routes to login carrying the
 * current page as returnUrl.
 */
function toSupportLot(
    lotId: string,
    result: SelectedLotState['result'] | undefined,
): VbSupportLot | null {
    if (!lotId) {
        return null;
    }
    if (!result || result instanceof Error || result.lotId !== lotId) {
        return { id: lotId };
    }
    return {
        id: lotId,
        number:
            result.number != null
                ? `${result.number}${result.suffix ?? ''}`
                : undefined,
        title: result.title ?? undefined,
    };
}

@Component({
    selector: 'vb-chatbox',
    templateUrl: './chatbox.component.html',
    styleUrls: ['./chatbox.component.scss'],
})
export class ChatboxComponent implements OnInit, OnDestroy {
    open: boolean = false;
    sending: boolean = false;
    error: VbSupportSendError | null = null;
    subjects: VbSupportSubject[] = FALLBACK_SUPPORT_SUBJECTS;
    lot: VbSupportLot | null = null;
    presetSubjectKey: string | null = null;
    fromLine: string = '';

    readonly phone: string = SUPPORT_PHONE_DISPLAY;
    readonly phoneHref: string = SUPPORT_PHONE_HREF;
    /** No published support hours yet — the call card carries the office
     *  address (from the legacy widget) until hours exist to show. */
    readonly callRows: VbSupportHoursRow[] = [
        { label: 'Address', value: '803 Main Street, Dartmouth NS B2W 3T4' },
    ];

    private username: string = '';
    private auctionId: string = '';
    private invoiceId: string = '';
    private readonly lotId$: BehaviorSubject<string> =
        new BehaviorSubject<string>('');
    /** HTTP status of the last failed send — a 401 turns the next primary
     *  click into the sign-in redirect instead of a retry POST. */
    private lastErrorStatus: number | null = null;
    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private router: Router,
        private store: Store<AppState>,
        private chatBoxService: ChatBoxService,
        private crudService: CrudService,
        private notificationService: NotificationsService,
    ) {}

    ngOnInit(): void {
        combineLatest([this.lotId$, this.store.select(selectSelectedLotState)])
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                ([lotId, state]: [string, SelectedLotState | undefined]) => {
                    this.lot = toSupportLot(lotId, state?.result);
                },
            );
        this.syncRouteContext(this.router.url);
        this.router.events
            .pipe(
                filter(
                    (event: RouterEvent): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((event: NavigationEnd) => {
                // The page under the panel changed — close and re-anchor the
                // context so a stale lot/invoice never rides along.
                this.open = false;
                this.presetSubjectKey = null;
                this.syncRouteContext(event.urlAfterRedirects ?? event.url);
            });

        this.store
            .select<User | null>(selectUser)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: User | null) => {
                this.username = (user?.['username'] as string) ?? '';
                this.fromLine = this.toFromLine(user);
            });

        // Direct opens (lot page "Message seller", invoice "Message") skip
        // the subject list by presetting the page's subject — launcher opens
        // never do, so the lot question stays a list option there.
        this.chatBoxService.openChatBox$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.lot) {
                    this.presetSubjectKey = 'ASK_ABOUT_LOT';
                } else if (this.invoiceId) {
                    this.presetSubjectKey = 'ISSUE_WITH_ORDER';
                }
                this.open = true;
            });

        this.loadTopics();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onOpenChange(open: boolean): void {
        this.open = open;
        if (!open) {
            // A later launcher click is a site-wide entry again.
            this.presetSubjectKey = null;
        }
    }

    onSend(message: VbSupportMessage): void {
        if (this.lastErrorStatus === 401) {
            // "Sign in and send" — carry the current page so login returns
            // the user here; the widget keeps the draft while it stays alive.
            this.open = false;
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: this.router.url },
            });
            return;
        }

        this.sending = true;
        this.error = null;

        const body: Record<string, unknown> = {
            comments: message.body,
            topic: message.subjectKey,
            username: this.username,
            status: 'Unread',
        };
        if (message.subjectKey === 'ASK_ABOUT_LOT') {
            body['auctionId'] = this.auctionId;
            body['lotId'] = message.lotId ?? '';
        }
        if (message.subjectKey === 'ISSUE_WITH_ORDER' && this.invoiceId) {
            body['invoiceId'] = this.invoiceId;
        }

        this.crudService.addData(USER_MESSAGES_ENDPOINT, body).subscribe({
            next: () => {
                this.lastErrorStatus = null;
                this.error = null;
                this.sending = false;
            },
            error: (err: HttpErrorResponse) => {
                this.lastErrorStatus = err?.status ?? 0;
                this.error = this.toSendError(err);
                this.sending = false;
            },
        });
    }

    /** What page are we on? Lot pages bind [lot] (panel opens straight into
     *  compose with the lot attached); view-invoice pages stash the invoice
     *  id for the payload and the direct-open preset. */
    private syncRouteContext(url: string): void {
        const path: string = url.split('?')[0].split('#')[0];
        this.invoiceId = path.includes('/view-invoice/')
            ? path.split('/view-invoice/')[1]
            : '';
        this.auctionId = path.includes('/auctions/')
            ? path.split('/auctions/')[1].split('/')[0]
            : '';

        this.lotId$.next(path.includes('/lot/') ? path.split('/lot/')[1] : '');
    }

    private loadTopics(): void {
        this.notificationService
            .getUserMessageTopics()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: GetUserMessageTopics200Response) => {
                    const subjects: VbSupportSubject[] =
                        mapTopicsToSupportSubjects(res.data);
                    if (subjects.length) {
                        this.subjects = subjects;
                    }
                },
                error: () => {
                    // Keep the static fallback rows.
                },
            });
    }

    /** "username · First Last" per the handoff's FROM row. */
    private toFromLine(user: User | null): string {
        if (!user) {
            return '';
        }
        const username: string = (user['username'] as string) ?? '';
        const name: string = [user['firstname'], user['lastname']]
            .filter(Boolean)
            .join(' ');
        if (username && name) {
            return `${username} · ${name}`;
        }
        return username || name || ((user['email'] as string) ?? '');
    }

    private toSendError(err: HttpErrorResponse): VbSupportSendError {
        const offline: boolean =
            typeof navigator !== 'undefined' && !navigator.onLine;
        if (offline || err?.status === 0) {
            return { message: SEND_ERROR_OFFLINE };
        }
        if (err?.status === 401) {
            return {
                message: SEND_ERROR_SESSION,
                retryLabel: 'Sign in and send',
            };
        }
        if (err?.status === 429) {
            return { message: SEND_ERROR_RATE_LIMIT };
        }
        return { message: SEND_ERROR_SERVER };
    }
}
